<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router';
import {
  TaskStatus,
  type ContractGenerationTemplateVariantInDb,
  type ContractGenerationTemplateVariantSummary,
  type ContractGenerationTemplateInDb,
  type ContractGenerationTemplateRoleShareInDb,
  type ContractGenerationTemplateDraftability,
  type ContractGenerationTemplateQuestionInDb,
  type ContractGenerationTemplateQuestionsResponse,
  type ContractGenerationVariantAnswersSummary,
} from '@/api';
import { useTemplateValidationPolling } from '@/composables/contractGeneration/useTemplateValidationPolling';
import TemplateVariantUploadModal from '@/components/contractGeneration/templates/TemplateVariantUploadModal.vue';
import ShareTemplateModal from '@/components/contractGeneration/templates/ShareTemplateModal.vue';
import TemplatingBreadcrumb from '@/components/contractGeneration/TemplatingBreadcrumb.vue';
import TemplateQuestionsSection from '@/components/contractGeneration/questions/TemplateQuestionsSection.vue';
import VariantAnswersModal from '@/components/contractGeneration/questions/VariantAnswersModal.vue';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountPill from '@/components/design/base/GaiaCountPill.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaDrawer from '@/components/design/base/GaiaDrawer.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaStatusIndicator from '@/components/design/base/GaiaStatusIndicator.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaCheckMenu from '@/components/design/composite/GaiaCheckMenu.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import GaiaVariantChip from '@/components/design/domain/templating/GaiaVariantChip.vue';
import GaiaVaultFilePdfPreview from '@/components/design/domain/fileVault/GaiaVaultFilePdfPreview.vue';
import { templateValidationChipMap } from '@/components/design/statusMaps';
import { TemplatesService } from '@/services/contractGeneration/templatesService';
import { TemplateQuestionsService } from '@/services/contractGeneration/templateQuestionsService';
import { TemplateVariantsService } from '@/services/contractGeneration/templateVariantsService';
import { TemplateShareService } from '@/services/templateShareService';
import { useContractGenerationStore } from '@/stores/contractGenerationStore';
import { extractErrorDetail } from '@/utils/errorHandler';
import { ACCESS_LEVELS, formatAccessPermissions } from '@/utils/accessPermission';
import { describeConstraints, draftBlockedMessage } from '@/utils/contractGeneration/questions';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  gaiaAccent,
  gaiaContentWidth,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaShadow,
  gaiaStatus,
} from '@/components/design/themes/tokens';
import { templateVariantRoute } from '@/router/links';

const route = useRoute();
const router = useRouter();
const message = useGaiaToast();
const store = useContractGenerationStore();

const templateUid = computed(() => {
  const value = route.params.uid;
  return typeof value === 'string' ? value : '';
});

const template = ref<ContractGenerationTemplateInDb | null>(null);
const variants = ref<ContractGenerationTemplateVariantSummary[]>([]);
const loading = ref<boolean>(false);
const error = ref<string | null>(null);

const editedName = ref<string>('');
const editedDescription = ref<string>('');
const savingMetadata = ref<boolean>(false);

const uploadModalVisible = ref<boolean>(false);
const showShareModal = ref<boolean>(false);

// Shared roles
const sharedRoleList = ref<ContractGenerationTemplateRoleShareInDb[]>([]);
const loadingSharedRoles = ref<boolean>(false);
const removingShareRoleUid = ref<string | null>(null);
const confirmRemoveRoleUid = ref<string | null>(null);
const editingShareRoleUid = ref<string | null>(null);
const editPermissionBitmask = ref<number>(0);
const savingSharePermission = ref<boolean>(false);

// Template + variant deletes run through inline confirm states, not popconfirms.
const confirmingTplDelete = ref<boolean>(false);
const deletingTpl = ref<boolean>(false);
const confirmDeleteVariantUid = ref<string | null>(null);
const deletingVariantUid = ref<string | null>(null);

/**
 * Which variant is being previewed in the drawer. The drawer renders the
 * underlying source `.docx` directly via `GaiaVaultFilePdfPreview`, using the
 * `vault_file_uid` + `vault_file_revision_uid` the backend now ships on
 * each summary so we don't have to re-implement rendering.
 */
const previewFile = ref<ContractGenerationTemplateVariantSummary | null>(null);

// Drawer width: sized like the Compose step's preview panel (~45% viewport)
// but clamped so it stays readable on narrow screens and doesn't overflow
// on very wide ones.
const previewDrawerWidth = computed<number>(() => {
  if (typeof window === 'undefined') return 720;
  return Math.min(960, Math.max(480, Math.round(window.innerWidth * 0.5)));
});

const existingVariantNames = computed(() => variants.value.map(variant => variant.name));

const isDirty = computed(() => {
  if (!template.value) return false;
  return (
    editedName.value.trim() !== template.value.name ||
    (editedDescription.value.trim() || null) !== (template.value.description ?? null)
  );
});

// ──────────────────────────────
// Questions — what this template asks, and what each document
// covers. One read fills both, and so does every write.
// ──────────────────────────────

const questions = ref<ContractGenerationTemplateQuestionInDb[]>([]);
const draftability = ref<ContractGenerationTemplateDraftability | null>(null);
const variantAnswers = ref<ContractGenerationVariantAnswersSummary[]>([]);
const answersVariant = ref<ContractGenerationVariantAnswersSummary | null>(null);

/** What each document narrows on, by uid — computed once per state change
 *  rather than per card, since every card reads it twice. */
const answerSummaries = computed<Record<string, { question: string; value: string }[]>>(() =>
  Object.fromEntries(
    variantAnswers.value.map(row => [row.variant_uid, describeConstraints(row, questions.value)])
  )
);

/** Required questions this document still owes — the only blocking kind. */
function missingRequiredCount(variantUid: string): number {
  const entry = variantAnswers.value.find(row => row.variant_uid === variantUid);
  return (entry?.unanswered_required_question_uids ?? []).length;
}

/**
 * Why nothing can be drafted from this template right now — most often a
 * version that hasn't answered a required question. Shown on the buttons that
 * would otherwise start a draft and immediately fail.
 */
const blockedReason = computed<string | null>(() => draftBlockedMessage(draftability.value));

/** A write answers with the whole state — adopt it, never re-read. */
function adoptQuestionsState(state: ContractGenerationTemplateQuestionsResponse): void {
  questions.value = state.questions ?? [];
  draftability.value = state.draftability;
  variantAnswers.value = state.variants ?? [];
}

async function loadQuestions(requestedUid: string): Promise<void> {
  try {
    const state = await TemplateQuestionsService.list(requestedUid);
    if (requestedUid !== templateUid.value) return;
    adoptQuestionsState(state);
  } catch (error_) {
    logger.warn('Failed to load the template’s questions:', error_);
  }
}

/**
 * Re-read before opening. The editor's fields are shaped by each question's
 * type, so a question deleted and recreated with a different type — here or in
 * another tab — would otherwise have the editor send the wrong shape and take
 * a 422 naming a question that looks right on screen.
 */
async function openAnswers(variantUid: string): Promise<void> {
  await loadQuestions(templateUid.value);
  answersVariant.value = variantAnswers.value.find(row => row.variant_uid === variantUid) ?? null;
}

async function load(): Promise<void> {
  const requestedUid = templateUid.value;
  if (!requestedUid) return;

  loading.value = true;
  error.value = null;

  try {
    const [t, files] = await Promise.all([
      TemplatesService.getById(requestedUid),
      TemplateVariantsService.list(requestedUid),
    ]);
    // Pin to the uid we requested: if the user navigated to another template
    // while this was in flight, drop the stale response so we don't clobber
    // the current template's data.
    if (requestedUid !== templateUid.value) return;
    template.value = t;
    editedName.value = t.name;
    editedDescription.value = t.description ?? '';
    variants.value = files;
    void loadSharedRoles(requestedUid);
    void loadQuestions(requestedUid);
  } catch (error_) {
    if (requestedUid !== templateUid.value) return;
    error.value = error_ instanceof Error ? error_.message : 'Failed to load template.';
  } finally {
    if (requestedUid === templateUid.value) loading.value = false;
  }
}

async function loadSharedRoles(requestedUid: string = templateUid.value): Promise<void> {
  if (!requestedUid) return;
  loadingSharedRoles.value = true;
  try {
    const roles = await TemplateShareService.getRoleSharesByTemplate(requestedUid);
    // Same staleness guard — a slow response for a previous template must not
    // overwrite the shares of the one now on screen.
    if (requestedUid !== templateUid.value) return;
    sharedRoleList.value = roles;
  } catch (error_) {
    if (requestedUid !== templateUid.value) return;
    message.error(error_ instanceof Error ? error_.message : 'Failed to load shared roles.');
    sharedRoleList.value = [];
  } finally {
    if (requestedUid === templateUid.value) loadingSharedRoles.value = false;
  }
}

// ──────────────────────────────────────────────────────────
// Async template validation status (per-variant)
// ──────────────────────────────────────────────────────────

function isVariantValidating(variant: { template_status?: TaskStatus | null }): boolean {
  return (
    variant.template_status === TaskStatus.PENDING || variant.template_status === TaskStatus.RUNNING
  );
}

function isVariantReady(variant: { template_status?: TaskStatus | null }): boolean {
  return variant.template_status === TaskStatus.DONE;
}

function isVariantErrored(variant: { template_status?: TaskStatus | null }): boolean {
  return variant.template_status === TaskStatus.FAILED;
}

function variantStatusChip(variant: { template_status?: TaskStatus | null }): {
  tone: 'neutral' | 'accent' | 'success' | 'warning' | 'alert';
  label: string;
} | null {
  if (!variant.template_status) return null;
  return templateValidationChipMap[variant.template_status];
}

const anyVariantValidating = computed<boolean>(() =>
  variants.value.some(variant => isVariantValidating(variant))
);

/** Cheap refresh of just the variant list — used by the polling tick while
 *  the worker chews through async validation. */
async function refreshVariants(): Promise<void> {
  if (!templateUid.value) return;
  const requestedUid = templateUid.value;
  try {
    const files = await TemplateVariantsService.list(requestedUid);
    if (requestedUid !== templateUid.value) return;
    variants.value = files;
  } catch (error_) {
    logger.warn('Background refresh of template variants failed:', error_);
  }
}

useTemplateValidationPolling(anyVariantValidating, refreshVariants);

async function handleRemoveSharedRole(roleUid: string): Promise<void> {
  if (!templateUid.value || removingShareRoleUid.value) return;
  confirmRemoveRoleUid.value = null;
  removingShareRoleUid.value = roleUid;
  try {
    await TemplateShareService.removeRoleShare(templateUid.value, roleUid);
    message.success('Role removed from share');
    await loadSharedRoles();
  } catch (error_) {
    message.error(error_ instanceof Error ? error_.message : 'Failed to remove share');
  } finally {
    removingShareRoleUid.value = null;
  }
}

function openEditRoleSharePopover(share: ContractGenerationTemplateRoleShareInDb): void {
  editingShareRoleUid.value = share.role.uid;
  editPermissionBitmask.value = share.permissions;
}

function closeEditSharePopover(): void {
  editingShareRoleUid.value = null;
}

const editPermissionOptions = computed(() =>
  ACCESS_LEVELS.map(level => ({
    key: String(level.value),
    label: level.label,
    checked: editPermissionBitmask.value === level.value,
  }))
);

function toggleEditPermissionFlag(key: string): void {
  editPermissionBitmask.value = Number(key);
}

async function saveEditSharePermission(): Promise<void> {
  if (!templateUid.value || !editingShareRoleUid.value || savingSharePermission.value) return;
  if (editPermissionBitmask.value === 0) {
    message.warning('Choose an access level');
    return;
  }
  savingSharePermission.value = true;
  try {
    await TemplateShareService.updateRoleSharePermissions(
      templateUid.value,
      editingShareRoleUid.value,
      editPermissionBitmask.value
    );
    message.success('Access updated');
    closeEditSharePopover();
    await loadSharedRoles();
  } catch (error_) {
    message.error(error_ instanceof Error ? error_.message : 'Failed to update permissions');
  } finally {
    savingSharePermission.value = false;
  }
}

function permissionDisplay(permissions: number): string {
  return formatAccessPermissions(permissions);
}

async function saveMetadata(): Promise<void> {
  if (!template.value || !isDirty.value) return;
  savingMetadata.value = true;

  try {
    const updated = await TemplatesService.update(template.value.uid, {
      name: editedName.value.trim(),
      description: editedDescription.value.trim() || null,
    });
    template.value = updated;
    store.upsertTemplate(updated);
    message.success('Template updated.');
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to update template.'));
  } finally {
    savingMetadata.value = false;
  }
}

async function deleteVariant(variant: ContractGenerationTemplateVariantSummary): Promise<void> {
  if (!templateUid.value) return;
  confirmDeleteVariantUid.value = null;
  deletingVariantUid.value = variant.uid;
  try {
    await TemplateVariantsService.delete(templateUid.value, variant.uid);
    variants.value = variants.value.filter(existing => existing.uid !== variant.uid);
    if (previewFile.value?.uid === variant.uid) {
      previewFile.value = null;
    }
    message.success(`Variant “${variant.name}” deleted.`);
    // One document fewer: its answers are gone and the verdict may have moved.
    void loadQuestions(templateUid.value);
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to delete template variant.'));
  } finally {
    deletingVariantUid.value = null;
  }
}

async function deleteTemplate(): Promise<void> {
  if (!template.value) return;
  deletingTpl.value = true;
  try {
    await store.deleteTemplate(template.value.uid);
    message.success('Template deleted.');
    void router.push({ name: 'contract-generation-templates' });
  } catch (error_) {
    message.error(error_ instanceof Error ? error_.message : 'Failed to delete template.');
  } finally {
    deletingTpl.value = false;
    confirmingTplDelete.value = false;
  }
}

/** Strip the detail-view's extra fields so we can store create/update results
 *  in our `Summary[]`-typed list. */
function toSummary(
  file: ContractGenerationTemplateVariantInDb
): ContractGenerationTemplateVariantSummary {
  return {
    uid: file.uid,
    created_at: file.created_at,
    updated_at: file.updated_at,
    name: file.name,
    file_name: file.file_name,
    mime_type: file.mime_type,
    vault_file_uid: file.vault_file_uid,
    vault_file_revision_uid: file.vault_file_revision_uid,
    // Carry the async validation status through so the per-card badge picks
    // up the freshly-uploaded "in_progress" state immediately and the page's
    // polling hook starts ticking until the worker finishes.
    template_status: file.template_status ?? null,
  };
}

function handleUploaded(file: ContractGenerationTemplateVariantInDb): void {
  // A new document answers nothing yet, and the upload endpoint answers with
  // the variant rather than the verdict — so this is one of the few places a
  // re-read is the right move.
  void loadQuestions(templateUid.value);
  const summary = toSummary(file);
  const existingIndex = variants.value.findIndex(existing => existing.uid === summary.uid);
  if (existingIndex === -1) {
    variants.value = [...variants.value, summary];
  } else {
    variants.value[existingIndex] = summary;
  }
}

function startWizard(variantUid: string): void {
  void router.push({
    name: 'contract-generation-wizard',
    params: { templateUid: templateUid.value },
    query: { variant: variantUid },
  });
}

function openAuthoring(variant: ContractGenerationTemplateVariantSummary): void {
  void router.push({
    name: 'contract-generation-template-author',
    params: { uid: templateUid.value, variantUid: variant.uid },
  });
}

function variantDetailRoute(variant: ContractGenerationTemplateVariantSummary): RouteLocationRaw {
  return templateVariantRoute(templateUid.value, variant.uid);
}

function openVariantDetail(variant: ContractGenerationTemplateVariantSummary): void {
  void router.push(variantDetailRoute(variant));
}

/**
 * Whole variant card is clickable, but interactive descendants (action
 * buttons, the confirm overlay) still own their own clicks.
 */
function handleVariantCardClick(
  variant: ContractGenerationTemplateVariantSummary,
  e: MouseEvent
): void {
  const target = e.target as HTMLElement;
  if (target.closest('button') || target.closest('.variant-overlay')) return;
  openVariantDetail(variant);
}

function handleVariantCardKeydown(
  variant: ContractGenerationTemplateVariantSummary,
  e: KeyboardEvent
): void {
  if (e.key !== 'Enter' && e.key !== ' ') return;
  const target = e.target as HTMLElement;
  if (target.closest('button')) return;
  e.preventDefault();
  openVariantDetail(variant);
}

function openPreview(variant: ContractGenerationTemplateVariantSummary): void {
  if (!variant.vault_file_uid) {
    message.warning('No source file available for this variant yet.');
    return;
  }
  previewFile.value = variant;
}

function closePreview(): void {
  previewFile.value = null;
}

function goBack(): void {
  void router.push({ name: 'contract-generation-templates' });
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

onMounted(() => {
  void load();
});

watch(templateUid, () => {
  void load();
});

// --- chrome ------------------------------------------------------------------
const fsMeta = gaiaFontSize.meta;
const fwSemibold = String(gaiaFontWeight.semibold);
const cardBorder = gaiaNeutral.borderCard;
const surface = gaiaNeutral.surface;
const dividerColor = gaiaNeutral.borderSoft;
const dashedBorder = gaiaNeutral.borderDashedSoft;
const dashedSurface = gaiaNeutral.surfaceSoft;
const inkColor = gaiaNeutral.ink;
const onInk = gaiaNeutral.surface;
const tileSurface = gaiaNeutral.surfaceMuted;
const tileColor = gaiaNeutral.textTertiary;
const accentColor = gaiaAccent.base;
const dangerColor = gaiaStatus.alert;
const alertBorder = gaiaStatus.alertBorder;
const alertSurfaceColor = gaiaStatus.alertSurface;
const cardHoverShadow = gaiaShadow.card;
const borderHover = gaiaNeutral.borderHoverSoft;
const shimmerBase = gaiaNeutral.surfaceMuted;
const shimmerHighlight = gaiaNeutral.surfaceSoft;
const busySurface = 'rgba(250, 250, 249, 0.92)';
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.record">
    <template #breadcrumb>
      <templating-breadcrumb :template-uid="templateUid" :template-name="template?.name" />
    </template>
    <n-flex vertical :size="26">
      <!-- Loading: the artboard's page shimmer — eyebrow, title, meta, then a
           card-sized block where the settings card will land. -->
      <div v-if="loading" class="page-skeleton">
        <div class="shimmer" style="width: 120px" />
        <div class="shimmer tall" style="width: 340px" />
        <div class="shimmer" style="width: 460px" />
        <div class="shimmer block" />
      </div>

      <gaia-empty-state v-else-if="error" status="alert" title="This template couldn’t be loaded">
        It may have been deleted, or you no longer have access to it.
        <template #meta>{{ error }}</template>
        <template #actions>
          <gaia-button type="primary" size="small" @click="() => load()">Retry</gaia-button>
          <gaia-button size="small" @click="goBack">Back to Templates</gaia-button>
        </template>
      </gaia-empty-state>

      <template v-else-if="template">
        <!-- Hero: the ink mark, eyebrow, serif title and one meta line. -->
        <n-flex vertical :size="14">
          <n-flex align="flex-start" :size="[20, 16]">
            <n-flex align="flex-start" :size="16" :style="{ minWidth: 0, flex: '1 1 380px' }">
              <div class="hero-mark">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M4 2.5h5l3 3v8H4z" />
                  <path d="M9 2.5v3h3" />
                  <path d="M5.8 9.2 6.8 12l1.2-2.6L9.2 12l1-2.8" />
                </svg>
              </div>
              <n-flex vertical :size="6" :style="{ minWidth: 0 }">
                <gaia-text variant="label" size="micro" tone="muted">Contract template</gaia-text>
                <gaia-header
                  :level="1"
                  variant="display"
                  size="screen"
                  :style="{ margin: 0, lineHeight: 1.1 }"
                >
                  {{ template.name }}
                </gaia-header>
                <n-flex align="center" :size="9" class="hero-meta">
                  <gaia-text v-if="template.description" size="dense" tone="tertiary">
                    {{ template.description }}
                  </gaia-text>
                  <span v-if="template.description" class="meta-dot">·</span>
                  <gaia-text size="dense" tone="tertiary">
                    {{ variants.length }} variant{{ variants.length === 1 ? '' : 's' }}
                  </gaia-text>
                  <span class="meta-dot">·</span>
                  <gaia-text
                    size="dense"
                    tone="tertiary"
                    :style="{ fontVariantNumeric: 'tabular-nums' }"
                  >
                    Created {{ formatDate(template.created_at) }}
                  </gaia-text>
                </n-flex>
              </n-flex>
            </n-flex>

            <n-flex align="center" :size="10" :style="{ marginLeft: 'auto' }">
              <gaia-delete-button
                variant="label"
                subject="template"
                @click="confirmingTplDelete = true"
              />
            </n-flex>
          </n-flex>

          <div v-if="deletingTpl" class="confirm-shell">
            <gaia-table-row-state variant="busy" note="Undo not available">
              Deleting “{{ template.name }}”…
            </gaia-table-row-state>
          </div>
          <div v-else-if="confirmingTplDelete" class="confirm-shell">
            <gaia-table-row-state
              variant="confirm"
              title="Delete this template and all of its variants?"
              confirm-label="Delete template"
              @cancel="confirmingTplDelete = false"
              @confirm="deleteTemplate"
            >
              Drafts made from it are deleted too. Documents already saved are kept.
            </gaia-table-row-state>
          </div>
        </n-flex>

        <!-- Settings card: rename / describe. -->
        <section class="card">
          <n-flex vertical :size="3">
            <gaia-text size="subtitle" weight="semibold" tone="ink">Settings</gaia-text>
            <gaia-text size="dense" tone="tertiary">Rename or describe this template.</gaia-text>
          </n-flex>
          <div class="settings-grid">
            <gaia-form-field label="Name">
              <gaia-input
                v-model:value="editedName"
                placeholder="Template name"
                style="width: 100%"
              />
            </gaia-form-field>
            <gaia-form-field label="Description">
              <gaia-textarea
                v-model:value="editedDescription"
                placeholder="What is this template for?"
                :autosize="{ minRows: 2, maxRows: 4 }"
                style="width: 100%"
              />
            </gaia-form-field>
          </div>
          <n-flex align="center" :size="10">
            <gaia-text size="micro" tone="faint">
              Renaming does not affect documents already drafted.
            </gaia-text>
            <gaia-button
              type="primary"
              size="small"
              :disabled="!isDirty"
              :loading="savingMetadata"
              :style="{ marginLeft: 'auto' }"
              @click="saveMetadata"
            >
              Save changes
            </gaia-button>
          </n-flex>
        </section>

        <!-- Shared with roles -->
        <n-flex vertical :size="12">
          <n-flex align="flex-start" :size="12">
            <n-flex vertical :size="3">
              <n-flex align="baseline" :size="8">
                <gaia-text size="subtitle" weight="semibold" tone="ink">
                  Shared with roles
                </gaia-text>
                <gaia-count-pill>{{ sharedRoleList.length }}</gaia-count-pill>
              </n-flex>
              <gaia-text size="dense" tone="tertiary">
                Permission roles that have access to this template.
              </gaia-text>
            </n-flex>
            <gaia-button
              size="small"
              :style="{ marginLeft: 'auto' }"
              @click="showShareModal = true"
            >
              <template #icon>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                >
                  <circle cx="12" cy="3.5" r="1.8" />
                  <circle cx="4" cy="8" r="1.8" />
                  <circle cx="12" cy="12.5" r="1.8" />
                  <path d="M5.7 7.1 10.3 4.4M5.7 8.9l4.6 2.7" />
                </svg>
              </template>
              Share template
            </gaia-button>
          </n-flex>

          <n-flex v-if="loadingSharedRoles" align="center" justify="center" class="roles-loading">
            <gaia-spinner size="small" />
          </n-flex>

          <div v-else-if="sharedRoleList.length === 0" class="dashed-note">
            <svg
              width="17"
              height="17"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="dashed-note__icon"
            >
              <path d="M8 2.2 13.2 4.4v4c0 3-2.2 4.5-5.2 5.4C5 12.9 2.8 11.4 2.8 8.4v-4z" />
              <path d="M5.8 8l1.5 1.5 2.9-3" />
            </svg>
            <gaia-text size="dense" tone="tertiary">
              No roles have access yet. Use “Share template” to grant a role access.
            </gaia-text>
          </div>

          <n-flex v-else vertical :size="8">
            <div v-for="share in sharedRoleList" :key="share.role.uid">
              <div v-if="removingShareRoleUid === share.role.uid" class="confirm-shell">
                <gaia-table-row-state variant="busy">
                  Removing access for “{{ share.role.name }}”…
                </gaia-table-row-state>
              </div>
              <div v-else-if="confirmRemoveRoleUid === share.role.uid" class="confirm-shell">
                <gaia-table-row-state
                  variant="confirm"
                  :title="`Remove access for “${share.role.name}”?`"
                  confirm-label="Remove access"
                  @cancel="confirmRemoveRoleUid = null"
                  @confirm="handleRemoveSharedRole(share.role.uid)"
                >
                  Members of this role can no longer see or use the template.
                </gaia-table-row-state>
              </div>
              <div v-else class="role-card">
                <div class="role-tile">
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.4"
                    stroke-linecap="round"
                  >
                    <circle cx="8" cy="5.4" r="2.6" />
                    <path d="M2.8 13.4c.9-2.4 2.9-3.6 5.2-3.6s4.3 1.2 5.2 3.6" />
                  </svg>
                </div>
                <n-flex vertical :size="1" :style="{ minWidth: 0, flex: 1 }">
                  <gaia-text size="row" weight="semibold" tone="ink">
                    {{ share.role.name }}
                  </gaia-text>
                  <gaia-text v-if="share.role.description" truncate size="meta" tone="subtle">
                    {{ share.role.description }}
                  </gaia-text>
                </n-flex>
                <gaia-status-chip tone="success">
                  {{ permissionDisplay(share.permissions as number) }}
                </gaia-status-chip>
                <gaia-popover
                  placement="bottom-end"
                  :show="editingShareRoleUid === share.role.uid"
                  @update:show="(v: boolean) => !v && closeEditSharePopover()"
                >
                  <template #trigger>
                    <gaia-icon-button
                      :dimension="28"
                      quaternary
                      title="Change access"
                      class="inline-action"
                      @click.stop="openEditRoleSharePopover(share)"
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M3 13.2 12.2 4l-1.7-1.7L1.3 11.5 1 14z" />
                      </svg>
                    </gaia-icon-button>
                  </template>
                  <gaia-check-menu
                    :title="`Permissions for ${share.role.name}`"
                    :options="editPermissionOptions"
                    @toggle="toggleEditPermissionFlag"
                    @cancel="closeEditSharePopover"
                    @confirm="saveEditSharePermission"
                  />
                </gaia-popover>
                <gaia-icon-button
                  :dimension="28"
                  quaternary
                  title="Remove access"
                  class="inline-action danger"
                  @click.stop="confirmRemoveRoleUid = share.role.uid"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.2"
                    stroke-linecap="round"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </gaia-icon-button>
              </div>
            </div>
          </n-flex>
        </n-flex>

        <!-- What this template asks. Above the documents, because the page
             reads: here are the questions, here is what answers them. -->
        <template-questions-section
          :template-uid="templateUid"
          :questions="questions"
          :draftability="draftability"
          @updated="adoptQuestionsState"
        />

        <!-- Variants -->
        <n-flex vertical :size="12" data-tour="tpl.variants">
          <n-flex align="flex-start" :size="12">
            <n-flex vertical :size="3">
              <n-flex align="baseline" :size="8">
                <gaia-text size="subtitle" weight="semibold" tone="ink">Variants</gaia-text>
                <gaia-count-pill>{{ variants.length }}</gaia-count-pill>
              </n-flex>
              <gaia-text size="dense" tone="tertiary">
                Named editions of this template — a language, a country, a wording. Each links its
                own .docx with its extracted field schema.
              </gaia-text>
            </n-flex>
            <gaia-button
              type="primary"
              size="small"
              :style="{ marginLeft: 'auto' }"
              @click="uploadModalVisible = true"
            >
              <template #icon>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </template>
              Add variant
            </gaia-button>
          </n-flex>

          <gaia-empty-state v-if="variants.length === 0" dashed title="No variants yet">
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.3"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M3.2 4.6v7.6a1.2 1.2 0 0 0 1.2 1.2h5.4" />
                <path d="M6 2.2h3.4l2.6 2.6v6.4H6z" />
                <path d="M9.4 2.2v2.6H12" />
              </svg>
            </template>
            Upload a .docx and name it for what makes it different — “German”, “Freelancer
            (Germany)”, “Male wording”.
            <template #actions>
              <gaia-button type="primary" size="small" @click="uploadModalVisible = true">
                Add your first variant
              </gaia-button>
            </template>
          </gaia-empty-state>

          <div v-else class="variant-grid">
            <article
              v-for="variant in variants"
              :key="variant.uid"
              class="variant-card"
              role="button"
              tabindex="0"
              @click="(e: MouseEvent) => handleVariantCardClick(variant, e)"
              @keydown="(e: KeyboardEvent) => handleVariantCardKeydown(variant, e)"
            >
              <n-flex vertical :size="10" class="variant-card__body">
                <n-flex align="center" :size="8" :wrap="false" :style="{ minWidth: 0 }">
                  <gaia-variant-chip :name="variant.name" />
                  <gaia-status-chip
                    v-if="variantStatusChip(variant)"
                    :tone="variantStatusChip(variant)!.tone"
                  >
                    {{ variantStatusChip(variant)!.label }}
                  </gaia-status-chip>
                  <gaia-icon-button
                    :dimension="26"
                    quaternary
                    title="Delete variant"
                    class="inline-action danger"
                    :style="{ marginLeft: 'auto' }"
                    @click.stop="confirmDeleteVariantUid = variant.uid"
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M2.5 4h11M5.5 4V2.5h5V4M4 4l.7 9.5h6.6L12 4" />
                    </svg>
                  </gaia-icon-button>
                </n-flex>

                <n-flex align="center" :size="8" :wrap="false" :style="{ minWidth: 0 }">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="variant-card__file-icon"
                  >
                    <path d="M4 2.5h5l3 3v8H4z" />
                    <path d="M9 2.5v3h3" />
                  </svg>
                  <gaia-text
                    truncate
                    size="dense"
                    weight="semibold"
                    tone="ink"
                    :title="variant.file_name"
                  >
                    <router-link
                      :to="variantDetailRoute(variant)"
                      style="color: inherit; text-decoration: none"
                      @click.stop
                    >
                      {{ variant.file_name }}
                    </router-link>
                  </gaia-text>
                </n-flex>

                <!-- What this document narrows on. Only the questions it
                     actually answers: silence widens here rather than
                     excluding, so it earns a word, not a chip of its own. -->
                <n-flex v-if="questions.length > 0" align="center" :size="6" :wrap="true">
                  <gaia-tag
                    v-for="(answer, index) in answerSummaries[variant.uid] ?? []"
                    :key="index"
                    size="tiny"
                    round
                  >
                    {{ answer.question }}: {{ answer.value }}
                  </gaia-tag>
                  <gaia-text
                    v-if="(answerSummaries[variant.uid] ?? []).length === 0"
                    size="meta"
                    tone="subtle"
                  >
                    Answers nothing yet — fits every combination
                  </gaia-text>
                  <n-flex
                    v-if="missingRequiredCount(variant.uid) > 0"
                    align="center"
                    :size="6"
                    :wrap="false"
                  >
                    <gaia-status-indicator status="alert" />
                    <gaia-text size="meta" tone="alert">
                      {{ missingRequiredCount(variant.uid) }} required unanswered
                    </gaia-text>
                  </n-flex>
                  <gaia-link
                    :underline="false"
                    :style="{ marginLeft: 'auto' }"
                    @click.stop="openAnswers(variant.uid)"
                  >
                    Edit
                  </gaia-link>
                </n-flex>

                <n-flex align="center" :size="6">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.4"
                    stroke-linecap="round"
                    class="variant-card__meta-icon"
                  >
                    <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" />
                    <path d="M5 2v3M11 2v3M2.5 7h11" />
                  </svg>
                  <gaia-text
                    size="micro"
                    tone="faint"
                    :style="{ fontVariantNumeric: 'tabular-nums' }"
                  >
                    {{ formatDate(variant.created_at) }}
                  </gaia-text>
                </n-flex>
              </n-flex>

              <n-flex align="center" :size="6" class="variant-card__actions">
                <gaia-tooltip>
                  <template #trigger>
                    <gaia-icon-button
                      :dimension="28"
                      class="bordered-action"
                      @click.stop="openPreview(variant)"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.4"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M1.8 8S4 3.8 8 3.8 14.2 8 14.2 8 12 12.2 8 12.2 1.8 8 1.8 8z" />
                        <circle cx="8" cy="8" r="1.9" />
                      </svg>
                    </gaia-icon-button>
                  </template>
                  Preview
                </gaia-tooltip>
                <gaia-tooltip>
                  <template #trigger>
                    <gaia-icon-button
                      :dimension="28"
                      class="bordered-action"
                      @click.stop="openAuthoring(variant)"
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M3 13.2 12.2 4l-1.7-1.7L1.3 11.5 1 14z" />
                      </svg>
                    </gaia-icon-button>
                  </template>
                  Edit fields
                </gaia-tooltip>
                <gaia-tooltip>
                  <template #trigger>
                    <span :style="{ marginLeft: 'auto' }">
                      <gaia-button
                        type="primary"
                        size="small"
                        :disabled="!isVariantReady(variant) || blockedReason !== null"
                        @click.stop="startWizard(variant.uid)"
                      >
                        <template #icon>
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path d="M8.8 1.8 3.2 9h4l-.8 5.2L12.8 7h-4z" />
                          </svg>
                        </template>
                        Draft document
                      </gaia-button>
                    </span>
                  </template>
                  <template v-if="isVariantValidating(variant)">Validating template…</template>
                  <template v-else-if="isVariantErrored(variant)">Validation failed</template>
                  <!-- The whole template is held back, not this document: the
                       tooltip says which, so the author isn't left inspecting a
                       version that is perfectly fine. -->
                  <template v-else-if="blockedReason">{{ blockedReason }}</template>
                  <template v-else>Draft document</template>
                </gaia-tooltip>
              </n-flex>

              <!-- Delete runs inside the card: a confirm overlay, then a busy
                   veil — the grid never reflows mid-decision. -->
              <div v-if="confirmDeleteVariantUid === variant.uid" class="variant-overlay confirm">
                <gaia-text size="dense" tone="text">
                  <strong>Delete the “{{ variant.name }}” variant?</strong> This cannot be undone,
                  and drafts made from it are deleted too.
                </gaia-text>
                <n-flex :size="8">
                  <gaia-button size="small" @click.stop="confirmDeleteVariantUid = null">
                    Cancel
                  </gaia-button>
                  <gaia-button size="small" type="error" @click.stop="deleteVariant(variant)">
                    Delete variant
                  </gaia-button>
                </n-flex>
              </div>
              <div v-else-if="deletingVariantUid === variant.uid" class="variant-overlay busy">
                <gaia-spinner size="small" />
                <gaia-text size="dense" tone="tertiary">Deleting “{{ variant.name }}”…</gaia-text>
              </div>
            </article>

            <button type="button" class="variant-add" @click="uploadModalVisible = true">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              <span>Add variant</span>
            </button>
          </div>
        </n-flex>
      </template>
    </n-flex>

    <variant-answers-modal
      v-if="answersVariant"
      :show="answersVariant !== null"
      :template-uid="templateUid"
      :variant="answersVariant"
      :questions="questions"
      @update:show="(value: boolean) => !value && (answersVariant = null)"
      @saved="adoptQuestionsState"
    />

    <TemplateVariantUploadModal
      v-if="template"
      v-model:show="uploadModalVisible"
      :template-uid="template.uid"
      :existing-names="existingVariantNames"
      :questions="questions"
      @uploaded="handleUploaded"
    />

    <ShareTemplateModal
      v-if="template"
      v-model:show="showShareModal"
      :template-uid="template.uid"
      :template-name="template.name"
      @shared="() => loadSharedRoles()"
    />

    <gaia-drawer
      :show="previewFile !== null"
      :width="previewDrawerWidth"
      placement="right"
      :mask-closable="true"
      :title="previewFile ? `Preview — ${previewFile.file_name}` : 'Preview'"
      flush
      @update:show="(value: boolean) => (value ? null : closePreview())"
    >
      <div v-if="previewFile && previewFile.vault_file_uid" class="preview-drawer-body">
        <gaia-vault-file-pdf-preview
          :vault-file-uid="previewFile.vault_file_uid"
          :revision-uid="previewFile.vault_file_revision_uid ?? undefined"
        />
      </div>
    </gaia-drawer>
  </Layout>
</template>

<style scoped>
/* Page shimmer while the template loads. */
.page-skeleton {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 6px 0;
}

.shimmer {
  height: 11px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    v-bind(shimmerBase) 0%,
    v-bind(shimmerHighlight) 45%,
    v-bind(shimmerBase) 90%
  );
  background-size: 480px 100%;
  animation: tpl-detail-shimmer 1.5s linear infinite;
}

.shimmer.tall {
  height: 26px;
  border-radius: 6px;
}

.shimmer.block {
  height: 180px;
  border-radius: 10px;
  margin-top: 14px;
}

@keyframes tpl-detail-shimmer {
  from {
    background-position: -480px 0;
  }
  to {
    background-position: 480px 0;
  }
}

/* The template's own mark — the ink tile from the mock's hero. */
.hero-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 44px;
  height: 44px;
  margin-top: 3px;
  border-radius: 11px;
  background: v-bind(inkColor);
  color: v-bind(onInk);
}

.hero-meta {
  margin-top: 2px;
}

.meta-dot {
  color: v-bind(dashedBorder);
}

/* GaiaTableRowState is a table strip; on a page it gets a rounded shell. */
.confirm-shell {
  border: 1px solid v-bind(alertBorder);
  border-radius: 10px;
  overflow: hidden;
  background: v-bind(alertSurfaceColor);
}

.card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 22px;
  border: 1px solid v-bind(cardBorder);
  border-radius: 10px;
  background: v-bind(surface);
}

/* Name narrow, description wide — the mock's two-track settings form. */
.settings-grid {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(260px, 1.4fr);
  gap: 14px;
}

.roles-loading {
  padding: 24px 0;
}

/* The "nothing here yet" strip for roles — dashed, not a full empty state,
   because the section is secondary to the page. */
.dashed-note {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  border: 1px dashed v-bind(dashedBorder);
  border-radius: 10px;
  background: v-bind(dashedSurface);
}

.dashed-note__icon {
  flex: none;
  color: v-bind(tileColor);
}

.role-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 16px;
  border: 1px solid v-bind(cardBorder);
  border-radius: 10px;
  background: v-bind(surface);
}

.role-tile {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: v-bind(tileSurface);
  color: v-bind(tileColor);
}

.inline-action {
  color: v-bind(tileColor);
}

.inline-action.danger:hover {
  color: v-bind(dangerColor);
}

/* The mock's auto-fill card grid. */
.variant-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 14px;
}

.variant-card {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid v-bind(cardBorder);
  border-radius: 10px;
  background: v-bind(surface);
  cursor: pointer;
  text-align: left;
}

.variant-card:hover {
  border-color: v-bind(borderHover);
  box-shadow: v-bind(cardHoverShadow);
}

.variant-card__body {
  padding: 14px 16px 10px;
}

.variant-card__file-icon {
  flex: none;
  color: v-bind(accentColor);
}

.variant-card__meta-icon {
  flex: none;
  color: v-bind(tileColor);
}

.variant-card__actions {
  margin-top: auto;
  padding: 10px 16px 12px;
  border-top: 1px solid v-bind(dividerColor);
}

.bordered-action {
  border: 1px solid v-bind(cardBorder);
  background: v-bind(surface);
  color: v-bind(tileColor);
}

.bordered-action:hover {
  border-color: v-bind(borderHover);
  color: v-bind(inkColor);
}

/* Delete decision lives inside the card so the grid never reflows. */
.variant-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  border-radius: 10px;
  padding: 16px;
}

.variant-overlay.confirm {
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  background: v-bind(alertSurfaceColor);
  border: 1px solid v-bind(alertBorder);
}

.variant-overlay.busy {
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: v-bind(busySurface);
}

.variant-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 150px;
  border: 1.5px dashed v-bind(dashedBorder);
  border-radius: 10px;
  background: v-bind(dashedSurface);
  color: v-bind(tileColor);
  font-family: inherit;
  font-size: v-bind(fsMeta);
  font-weight: v-bind(fwSemibold);
  cursor: pointer;
}

.variant-add:hover {
  border-color: v-bind(borderHover);
  color: v-bind(inkColor);
}

.preview-drawer-body {
  height: 100%;
}
</style>
